import React from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import classNames from 'classnames'
import {Divider, Grid, List, Row, Typography} from 'antd'
import {PercentTrend} from '@/components/composite'
import {addZeroes, getAmountWithCurrency} from '@/utils'
import {IconTitle} from '@/components'
import {SettlementsStatus, TerminalType} from '@/constants'
import {TransactionListItemProps} from './props'
import styles from './styles.scss'

const {Title, Text} = Typography
const {Item} = List

export const TransactionSummaryListItem: React.FC<TransactionListItemProps> = observer(
    ({item, hasDivider, className, period, statusLabel, currency, type}) => {
        const {t} = useTranslation()
        const screens = Grid.useBreakpoint()
        const isMobile = screens.xs
        const isTabOrLaptop = screens.sm && screens.md && !screens.lg && !screens.xl
        
        const tooltipKey = type === TerminalType.SETTLEMENTS 
            ? 'TransactionSummaryListItem.settlementTooltipText' 
            : 'TransactionSummaryListItem.tooltipText'
        const tooltipForStatusObject = t(tooltipKey, {returnObjects: true})
        const tooltipForStatus = tooltipForStatusObject[item.status]
        const renderTransactionsCount = () => {
            if (type === TerminalType.SETTLEMENTS && item.status !== SettlementsStatus.sales) {
                return null
            }

            return (
                <Text>
                    {t('TransactionSummaryListItem.transactions')}: {item.count ?? '-'}
                </Text>
            )
        }

        return (
            <Item
                key={item.status}
                className={classNames(styles.item, className, {[styles.itemMobile]: isMobile})}
            >
                <div className={classNames(styles.content, {[styles.contentMobile]: isMobile})}>
                    <Row justify={'space-between'}>
                        <Text>
                            {statusLabel}
                            {tooltipForStatus && (
                                <IconTitle
                                    title={null}
                                    className={styles.infoIcon}
                                    tooltipText={tooltipForStatus}
                                />
                            )}
                        </Text>
                        {item.percent && item.percent !== 0 ? (
                            <PercentTrend
                                percent={item.percent}
                                tooltipText={t(
                                    'TransactionSummaryListItem.comparedToPreviousPeriod',
                                    {period}
                                )}
                            />
                        ) : null}
                    </Row>
                    <Title className={styles.title} level={3}>
                        {getAmountWithCurrency(addZeroes(item.amount), currency)}
                    </Title>
                    {renderTransactionsCount()}
                </div>
                {!isTabOrLaptop && hasDivider && (
                    <Divider
                        className={classNames(styles.divider, {[styles.dividerMobile]: isMobile})}
                        type={isMobile ? 'horizontal' : 'vertical'}
                    />
                )}
            </Item>
        )
    }
)
